import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Sparkles, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  {
    to: "/notre-univers",
    label: "Notre univers",
    children: [
      { to: "/notre-univers/presentation", label: "Présentation" },
      { to: "/notre-univers/approche", label: "Approche" },
    ],
  },
  { to: "/nos-activites", label: "Nos activités" },
  { to: "/enseignants", label: "Enseignants" },
  {
    to: "/contact",
    label: "Contact",
    children: [
      { to: "/equipe", label: "Équipe" },
      { to: "/job", label: "Job" },
    ],
  },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <span className="grid place-items-center w-10 h-10 rounded-2xl bg-sky-500 text-white shadow-lg shadow-sky-500/30 rotate-[-6deg]">
            <Sparkles className="w-5 h-5" />
          </span>
          <span className="font-heading font-extrabold text-2xl tracking-tight text-slate-900">
            Kwarizmix
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.children ? (
              <DropdownMenu key={l.label}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`nav-${l.to.replace(/\//g, "")}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-sky-600 hover:bg-sky-50 transition-colors focus:outline-none"
                  >
                    {l.label} <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="rounded-2xl">
                  <DropdownMenuItem
                    onClick={() => navigate(l.to)}
                    data-testid={`nav-${l.to.replace(/\//g, "")}-overview`}
                    className="cursor-pointer font-semibold"
                  >
                    {l.label}
                  </DropdownMenuItem>
                  {l.children.map((c) => (
                    <DropdownMenuItem
                      key={c.to}
                      onClick={() => navigate(c.to)}
                      data-testid={`nav-${c.to.replace(/\//g, "")}`}
                      className="cursor-pointer"
                    >
                      {c.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-sky-600 bg-sky-50"
                      : "text-slate-600 hover:text-sky-600 hover:bg-sky-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="navbar-user-menu"
                  className="hidden sm:grid place-items-center w-11 h-11 rounded-full bg-sky-500 text-white hover:brightness-110 transition"
                >
                  <User className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                <div className="px-3 py-2 text-sm">
                  <p className="font-bold text-slate-900">{user.name}</p>
                  <p className="text-slate-500 text-xs">{user.email}</p>
                </div>
                <DropdownMenuItem
                  data-testid="menu-dashboard"
                  onClick={() => navigate("/mon-compte")}
                  className="cursor-pointer"
                >
                  Mon compte
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    data-testid="menu-admin"
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer font-semibold text-sky-600"
                  >
                    Back-office
                  </DropdownMenuItem>
                )}
                {user.role === "animateur" && (
                  <DropdownMenuItem
                    data-testid="menu-animator"
                    onClick={() => navigate("/espace-animateur")}
                    className="cursor-pointer font-semibold text-sky-600"
                  >
                    Mes disponibilités
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="cursor-pointer text-rose-500"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/connexion"
              data-testid="navbar-login-button"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-sky-600 transition-colors"
            >
              Connexion
            </Link>
          )}

          <button
            className="lg:hidden grid place-items-center w-11 h-11 rounded-full bg-slate-100"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-1">
          {links.map((l) => (
            <div key={l.label}>
              <NavLink
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl font-semibold ${
                    isActive ? "bg-sky-50 text-sky-600" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
              {l.children && (
                <div className="ml-4 border-l border-slate-100 pl-2 mb-1">
                  {l.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mnav-${c.to.replace(/\//g, "")}`}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 rounded-xl text-sm font-medium ${
                          isActive ? "text-sky-600" : "text-slate-500"
                        }`
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-2 border-t border-slate-100 flex gap-2">
            {user ? (
              <>
                <Link
                  to="/mon-compte"
                  onClick={() => setOpen(false)}
                  className="flex-1 text-center px-4 py-3 rounded-xl bg-sky-500 text-white font-bold"
                >
                  Mon compte
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="px-4 py-3 rounded-xl bg-rose-50 text-rose-500 font-bold"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <Link
                to="/connexion"
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-4 py-3 rounded-xl bg-slate-900 text-white font-bold"
              >
                Connexion
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
